import { useId, useRef } from 'react';
import { apiErrorText, useGetFsnbResourceQuery } from '@shared/api';
import { fmtNum } from '@shared/lib/smetaLabels';
import { Drawer, DrawerBody, DrawerCloseButton, DrawerHeader } from '@shared/ui/Drawer';
import {
  categoryLabel,
  fullCode,
  machinePriceMissing,
  priceText,
  sourceLabel,
} from '../../normBaseLabels';
import {
  Err,
  Eyebrow,
  Grid,
  HeaderRow,
  Meta,
  Muted,
  MutedNote,
  Name,
  Section,
  SectionTitle,
  TableWrap,
  Title,
  Titles,
} from '../styles';

/** Карточка ресурса: цена и нормы, в которые он входит. */
export function ResourceCardDrawer({
  source,
  code,
  edition,
  onClose,
  onOpenWork,
}: {
  source: string;
  code: string;
  edition: string;
  onClose: () => void;
  onOpenWork?: (baseType: string, code: string) => void;
}) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const q = useGetFsnbResourceQuery({ source, code, edition });
  const card = q.data?.card;
  const d = card?.resource.price_details;
  return (
    <Drawer open onClose={onClose} labelledBy={`${uid}-title`} size="md" initialFocusRef={titleRef}>
      <DrawerHeader>
        <HeaderRow>
          <Titles>
            <Eyebrow>Ресурс · {sourceLabel(source)}</Eyebrow>
            <Title id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
              {code}
            </Title>
          </Titles>
          <DrawerCloseButton onClick={onClose} />
        </HeaderRow>
      </DrawerHeader>
      <DrawerBody>
        {q.isLoading && <Muted>Загрузка…</Muted>}
        {q.error && <Err>{apiErrorText(q.error, 'Ресурс не удалось открыть.')}</Err>}
        {card && (
          <>
            <Name>{card.resource.name}</Name>
            <Meta>
              <span>
                Ед. изм.: <strong>{card.resource.unit ?? '—'}</strong>
              </span>
              <span>
                Категория: <strong>{categoryLabel(card.resource.category)}</strong>
              </span>
            </Meta>
            <Section>
              <SectionTitle>Сметная цена (базисный уровень)</SectionTitle>
              {machinePriceMissing(card.resource.category, card.resource.cost, d) && (
                <Muted>
                  Цена машины в этой редакции не сохранена: её загрузили до раздела «Нормативная
                  база». В редакциях, загруженных архивом здесь, цена машины есть.
                </Muted>
              )}
              <Meta>
                <span>
                  <strong>{priceText(card.resource.cost, d)}</strong>
                </span>
                {typeof card.resource.opt_cost === 'number' && (
                  <span>
                    Оптовая: <strong>{priceText(card.resource.opt_cost, null)}</strong>
                  </span>
                )}
                {typeof d?.LabourMach === 'number' && d.LabourMach > 0 && (
                  <span>
                    Труд машиниста: <strong>{fmtNum(d.LabourMach)} чел.-ч</strong>
                    {typeof d.MachinistCategory === 'number'
                      ? `, разряд ${fmtNum(d.MachinistCategory)}`
                      : ''}
                  </span>
                )}
                {typeof d?.ElectricityCost === 'number' && d.ElectricityCost > 0 && (
                  <span>
                    Электроэнергия: <strong>{priceText(d.ElectricityCost, null)}</strong>
                  </span>
                )}
              </Meta>
            </Section>
            {source === 'fsbc' && (
              <Section>
                <SectionTitle>
                  Входит в нормы{card.used_in_total > 0 ? `: ${fmtNum(card.used_in_total)}` : ''}
                </SectionTitle>
                {card.used_in.length === 0 ? (
                  <Muted>В составе норм этой редакции ресурс не встречается.</Muted>
                ) : (
                  <TableWrap>
                    <Grid>
                      <thead>
                        <tr>
                          <th>Норма</th>
                          <th>Наименование</th>
                          <th className="num">Расход</th>
                        </tr>
                      </thead>
                      <tbody>
                        {card.used_in.map((u) => (
                          <tr
                            key={`${u.base_type}-${u.code}`}
                            className={onOpenWork ? 'clickable' : undefined}
                            onClick={onOpenWork ? () => onOpenWork(u.base_type, u.code) : undefined}
                          >
                            <td className="code">{fullCode(u.base_type, u.code)}</td>
                            <td>{u.name}</td>
                            <td className="num">
                              {u.quantity === null ? 'по проекту' : fmtNum(u.quantity)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Grid>
                  </TableWrap>
                )}
                {card.used_in_total > card.used_in.length && (
                  <MutedNote>
                    Показаны первые {card.used_in.length} из {fmtNum(card.used_in_total)}.
                  </MutedNote>
                )}
              </Section>
            )}
          </>
        )}
      </DrawerBody>
    </Drawer>
  );
}
